import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Apple, Loader2, Plus, Trash2, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import CropLoadResultView from '../../components/orchard/CropLoadResultView';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { computeCropLoad, MIN_SAMPLES, STAGE_LABEL } from '../../utils/cropLoad';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import type { CropLoadFormData, CropLoadList, CropLoadStage, TrunkMeasure } from '../../types/orchard';

/** A sample as typed: numbers stay strings until saved. */
interface SampleDraft {
  key: string;
  row_label: string;
  tree_label: string;
  trunk_value: string;
  counted_on: 'tree' | 'limb';
  limb_diameter_mm: string;
  fruit_count: string;
}

interface Draft {
  counted_on: string;
  stage: CropLoadStage | '';
  trunk_measure: TrunkMeasure;
  fruit_weight_g: string;
  bin_kg: string;
  loss_pct: string;
  notes: string;
  samples: SampleDraft[];
}

let keySeq = 0;
const newKey = () => `s${Date.now().toString(36)}${(keySeq += 1)}`;

const blankSample = (prev?: SampleDraft): SampleDraft => ({
  key: newKey(),
  // The same row and how the last tree was counted carry on: a count walks a row.
  row_label: prev?.row_label ?? '',
  tree_label: '',
  trunk_value: '',
  counted_on: prev?.counted_on ?? 'tree',
  limb_diameter_mm: '',
  fruit_count: '',
});

const str = (v: number | null | undefined) => (v == null ? '' : String(v));
const numOrNull = (v: string) => (v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v));

const draftKey = (areaId: number) => `cropLoadDraft:${areaId}`;

function readStoredDraft(areaId: number): Draft | null {
  try {
    const raw = localStorage.getItem(draftKey(areaId));
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

const input = 'w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const label = 'block text-xs font-medium text-neutral-600 mb-1';

/**
 * One crop load count of a cultivar area: sample trees, each with its trunk
 * measured and its fruit counted on the whole tree or on one measured limb.
 * The figure updates as it is typed; the server's is what is saved. A new
 * count is kept on the phone until it is saved, so a walk of the rows out of
 * signal loses nothing.
 */
export default function CropLoadCount() {
  const { id, countId } = useParams<{ id: string; countId?: string }>();
  const areaId = Number(id);
  const editingId = countId && countId !== 'new' ? Number(countId) : null;
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [list, setList] = useState<CropLoadList | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [restored, setRestored] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const focusKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await orchardService.getCropLoads(areaId);
      if (!res.success || !res.data) throw new Error('Could not load the cultivar area');
      const data = res.data;
      setList(data);
      if (editingId) {
        const c = data.counts.find((x) => x.id === editingId);
        if (!c) throw new Error('This count was not found. It may have been deleted.');
        setDraft({
          counted_on: c.counted_on,
          stage: c.stage ?? '',
          trunk_measure: c.trunk_measure,
          fruit_weight_g: str(c.fruit_weight_g),
          bin_kg: str(c.bin_kg),
          loss_pct: str(c.loss_pct),
          notes: c.notes ?? '',
          samples: c.samples.map((s) => ({
            key: newKey(),
            row_label: s.row_label ?? '',
            tree_label: s.tree_label ?? '',
            trunk_value: str(s.trunk_value),
            counted_on: s.counted_on,
            limb_diameter_mm: str(s.limb_diameter_mm),
            fruit_count: str(s.fruit_count),
          })),
        });
        return;
      }
      const stored = readStoredDraft(areaId);
      if (stored) {
        setDraft(stored);
        setRestored(true);
        return;
      }
      // Starts from the last count's settings, else the cultivar's recorded fruit weight and the usual bin.
      const prev = data.counts[0];
      setDraft({
        counted_on: getTodayInTz(timezone),
        stage: '',
        trunk_measure: prev?.trunk_measure ?? 'circumference_cm',
        fruit_weight_g: str(prev?.fruit_weight_g ?? data.defaults.fruit_weight_g),
        bin_kg: str(prev?.bin_kg ?? data.defaults.bin_kg),
        loss_pct: str(prev?.loss_pct ?? 0),
        notes: '',
        samples: [blankSample()],
      });
    } catch (e) {
      setError(errorMessage(e, 'Could not load the cultivar area'));
    }
  }, [areaId, editingId, timezone]);
  useEffect(() => { load(); }, [load]);

  // A new count is kept on the device until it is saved.
  useEffect(() => {
    if (!draft || editingId) return;
    try { localStorage.setItem(draftKey(areaId), JSON.stringify(draft)); } catch { /* storage full or blocked */ }
  }, [draft, areaId, editingId]);

  useEffect(() => {
    if (!focusKey.current) return;
    document.getElementById(`trunk-${focusKey.current}`)?.focus();
    focusKey.current = null;
  });

  const preview = useMemo(() => (draft && list ? computeCropLoad({
    trunk_measure: draft.trunk_measure,
    samples: draft.samples,
    tree_count: list.area.tree_count,
    area_hectares: list.area.area_hectares,
    fruit_weight_g: draft.fruit_weight_g,
    bin_kg: draft.bin_kg,
    loss_pct: draft.loss_pct,
  }) : null), [draft, list]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const setSample = (key: string, patch: Partial<SampleDraft>) =>
    setDraft((d) => (d ? { ...d, samples: d.samples.map((s) => (s.key === key ? { ...s, ...patch } : s)) } : d));
  const addSample = () => setDraft((d) => {
    if (!d) return d;
    const s = blankSample(d.samples[d.samples.length - 1]);
    focusKey.current = s.key;
    return { ...d, samples: [...d.samples, s] };
  });
  const removeSample = (key: string) =>
    setDraft((d) => (d ? { ...d, samples: d.samples.filter((s) => s.key !== key) } : d));

  const discardDraft = () => {
    try { localStorage.removeItem(draftKey(areaId)); } catch { /* ignore */ }
    setRestored(false);
    setDraft(null);
    load();
  };

  const back = () => navigate(`/orchard/cultivar-areas/${areaId}`);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    const body: CropLoadFormData = {
      counted_on: draft.counted_on,
      stage: draft.stage || null,
      trunk_measure: draft.trunk_measure,
      fruit_weight_g: numOrNull(draft.fruit_weight_g),
      bin_kg: numOrNull(draft.bin_kg),
      loss_pct: numOrNull(draft.loss_pct) ?? 0,
      notes: draft.notes.trim() || null,
      // A row nobody typed anything into is not a tree.
      samples: draft.samples
        .filter((s) => s.trunk_value.trim() || s.fruit_count.trim())
        .map((s) => ({
          row_label: s.row_label.trim() || null,
          tree_label: s.tree_label.trim() || null,
          trunk_value: numOrNull(s.trunk_value),
          counted_on: s.counted_on,
          limb_diameter_mm: s.counted_on === 'limb' ? numOrNull(s.limb_diameter_mm) : null,
          fruit_count: numOrNull(s.fruit_count),
        })),
    };
    try {
      if (editingId) await orchardService.updateCropLoad(editingId, body);
      else {
        await orchardService.createCropLoad(areaId, body);
        try { localStorage.removeItem(draftKey(areaId)); } catch { /* ignore */ }
      }
      back();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the count. It is kept on this device; try again when you have signal.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    setDeleting(true);
    try {
      await orchardService.deleteCropLoad(editingId);
      back();
    } catch (e) {
      setError(errorMessage(e, 'Could not delete the count'));
      setDeleting(false);
    }
  };

  const trunkUnit = draft?.trunk_measure === 'diameter_mm' ? 'mm' : 'cm';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={editingId ? 'Crop load count' : 'New crop load count'}
        subtitle={list?.area.name ?? 'Loading…'}
        icon={<Apple className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        )}

        {!draft && !error && (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-primary-600 animate-spin" /></div>
        )}

        {draft && list && preview && (
          <>
            {restored && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-blue-800">Carrying on with the count not yet saved on this device.</p>
                <Button size="sm" variant="outline" onClick={discardDraft}>Start again</Button>
              </div>
            )}

            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="cl-date" className={label}>Counted on</label>
                    <input id="cl-date" type="date" className={input} value={draft.counted_on}
                      onChange={(e) => set('counted_on', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="cl-stage" className={label}>Stage</label>
                    <select id="cl-stage" className={input} value={draft.stage}
                      onChange={(e) => set('stage', e.target.value as CropLoadStage | '')}>
                      <option value="">Not stated</option>
                      {(Object.keys(STAGE_LABEL) as CropLoadStage[]).map((k) => (
                        <option key={k} value={k}>{STAGE_LABEL[k]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cl-measure" className={label}>Trunks measured as</label>
                    <select id="cl-measure" className={input} value={draft.trunk_measure}
                      onChange={(e) => set('trunk_measure', e.target.value as TrunkMeasure)}>
                      <option value="circumference_cm">Circumference (cm, tape)</option>
                      <option value="diameter_mm">Diameter (mm, calipers)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="cl-weight" className={label}>Fruit weight at harvest (g)</label>
                    <input id="cl-weight" type="text" inputMode="decimal" className={input} value={draft.fruit_weight_g}
                      onChange={(e) => set('fruit_weight_g', e.target.value)} />
                    {list.defaults.fruit_weight_g != null && (
                      <p className="text-xs text-neutral-500 mt-1">
                        This cultivar's readings: {list.defaults.fruit_weight_g} g (median of {list.defaults.fruit_weight_readings})
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="cl-bin" className={label}>Net kg per bin</label>
                    <input id="cl-bin" type="text" inputMode="decimal" className={input} value={draft.bin_kg}
                      onChange={(e) => set('bin_kg', e.target.value)} />
                    {list.defaults.bin_kg != null && list.defaults.bin_name && (
                      <p className="text-xs text-neutral-500 mt-1">Usual bin: {list.defaults.bin_name}, {list.defaults.bin_kg} kg</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="cl-loss" className={label}>Still to lose (%)</label>
                    <input id="cl-loss" type="text" inputMode="decimal" className={input} value={draft.loss_pct}
                      onChange={(e) => set('loss_pct', e.target.value)} />
                    <p className="text-xs text-neutral-500 mt-1">Thinning to come, drop, hail, culls</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">
                  {list.area.tree_count != null
                    ? `Scaled to the area's ${list.area.tree_count.toLocaleString('en-AU')} trees.`
                    : 'The area has no tree count yet, so this gives fruit a tree only.'}
                  {' '}Measure the trunk at the same height on every tree, about 30 cm above the graft.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-neutral-700">Trees</h3>
                  <p className="text-xs text-neutral-500">
                    {preview.samples_counted} counted{preview.samples_counted < MIN_SAMPLES ? ` of at least ${MIN_SAMPLES}` : ''}
                  </p>
                </div>
                <ol className="space-y-3">
                  {draft.samples.map((s, i) => (
                    <li key={s.key} className="border border-neutral-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-neutral-500">Tree {i + 1}</span>
                        <button type="button" onClick={() => removeSample(s.key)} aria-label={`Remove tree ${i + 1}`}
                          className="p-1 text-neutral-400 hover:text-error-600 rounded">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                        <div>
                          <label htmlFor={`row-${s.key}`} className={label}>Row</label>
                          <input id={`row-${s.key}`} className={input} value={s.row_label}
                            onChange={(e) => setSample(s.key, { row_label: e.target.value })} />
                        </div>
                        <div>
                          <label htmlFor={`tree-${s.key}`} className={label}>Tree</label>
                          <input id={`tree-${s.key}`} className={input} value={s.tree_label}
                            onChange={(e) => setSample(s.key, { tree_label: e.target.value })} />
                        </div>
                        <div>
                          <label htmlFor={`trunk-${s.key}`} className={label}>Trunk ({trunkUnit})</label>
                          <input id={`trunk-${s.key}`} type="text" inputMode="decimal" className={input} value={s.trunk_value}
                            onChange={(e) => setSample(s.key, { trunk_value: e.target.value })} />
                        </div>
                        <div>
                          <label htmlFor={`on-${s.key}`} className={label}>Counted on</label>
                          <select id={`on-${s.key}`} className={input} value={s.counted_on}
                            onChange={(e) => setSample(s.key, { counted_on: e.target.value as 'tree' | 'limb' })}>
                            <option value="tree">Whole tree</option>
                            <option value="limb">One limb</option>
                          </select>
                        </div>
                        {s.counted_on === 'limb' ? (
                          <div>
                            <label htmlFor={`limb-${s.key}`} className={label}>Limb (mm)</label>
                            <input id={`limb-${s.key}`} type="text" inputMode="decimal" className={input} value={s.limb_diameter_mm}
                              onChange={(e) => setSample(s.key, { limb_diameter_mm: e.target.value })} />
                          </div>
                        ) : <div className="hidden sm:block" />}
                        <div>
                          <label htmlFor={`fruit-${s.key}`} className={label}>Fruit</label>
                          <input id={`fruit-${s.key}`} type="text" inputMode="numeric" className={input} value={s.fruit_count}
                            onChange={(e) => setSample(s.key, { fruit_count: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter' && i === draft.samples.length - 1) { e.preventDefault(); addSample(); } }} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
                <Button variant="outline" size="sm" onClick={addSample}>
                  <Plus className="h-4 w-4" />
                  Add tree
                </Button>
                <p className="text-xs text-neutral-500">
                  Leave Fruit blank to record a trunk only; it still counts towards the average trunk.
                  A limb count is scaled up by the limb's own cross-section.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <h3 className="text-sm font-semibold text-neutral-700">What it comes to</h3>
                <CropLoadResultView result={preview} />
                <div>
                  <label htmlFor="cl-notes" className={label}>Notes</label>
                  <textarea id="cl-notes" rows={2} className={input} value={draft.notes}
                    onChange={(e) => set('notes', e.target.value)} />
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary" isLoading={saving} onClick={save}>Save count</Button>
              <Button variant="outline" onClick={back}>Cancel</Button>
              {editingId && !confirmDelete && (
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
              {confirmDelete && (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-error-700">Delete this count? A crop estimate already made from it stays.</span>
                  <Button variant="danger" size="sm" isLoading={deleting} onClick={remove}>Yes, delete</Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>Keep</Button>
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
